import { Controller } from "@hotwired/stimulus"

// Generic collapsible card controller. State is persisted to localStorage
// under the key "admin-tracks:cards" (a JSON object keyed by `keyValue`) so
// it survives Turbo frame swaps — the detail renders inside
// <turbo-frame id="track_detail">, which re-renders on every track selection.
//
// Usage:
//   <div data-controller="collapsible"
//        data-collapsible-key-value="build-pipeline"
//        data-collapsible-open-value="true">
//     <button data-action="collapsible#toggle">
//       <svg data-collapsible-target="chevron" …/>
//       Title
//     </button>
//     <div data-collapsible-target="panel">…content…</div>
//   </div>
export default class extends Controller {
  static targets = ["panel", "chevron"]
  static values = { key: String, open: { type: Boolean, default: true } }

  connect() {
    const stored = this.#readStorage()
    // If the key exists in storage, honour it. If not, fall back to the
    // server-side default (openValue, which defaults to true).
    const isOpen =
      this.keyValue in stored ? stored[this.keyValue] : this.openValue
    this.#apply(isOpen)
  }

  toggle() {
    const isCurrentlyOpen = !this.panelTarget.classList.contains("hidden")
    const next = !isCurrentlyOpen
    this.#apply(next)
    this.#persist(next)
  }

  // ── private ─────────────────────────────────────────────────────────────

  #apply(open) {
    this.panelTarget.classList.toggle("hidden", !open)
    // Chevron: rotated (pointing down / open) when open, upright when closed.
    this.chevronTarget.classList.toggle("rotate-90", open)
  }

  #persist(open) {
    if (!this.keyValue) return
    const stored = this.#readStorage()
    stored[this.keyValue] = open
    try {
      localStorage.setItem("admin-tracks:cards", JSON.stringify(stored))
    } catch (_) {
      // Quota or private-mode error — ignore, state just won't persist.
    }
  }

  #readStorage() {
    try {
      return JSON.parse(localStorage.getItem("admin-tracks:cards") || "{}")
    } catch (_) {
      return {}
    }
  }
}
